<template>
  <div>
    <div class="p-4 bg-secondary text-white">
      <h4 class="uppercase mb-0">{{ $t('titles.scope') }}</h4>
    </div>
    <div class="p-4 pb-0">
      <!-- Public Note -->
      <div class="rounded border bg-gray-100 mb-4" v-if="currentScope.public_note">
        <div class="p-4 text-sm text-gray-700">
          <p class="text-danger">{{ $t('generic.pleaseNote') }}:</p>
          <div>{{currentScope.public_note}}</div>
        </div>
      </div>
      <div class="flex flex-col lg:flex-row lg:flex-wrap">
        <!-- Turret Unit -->
        <div class="form-group w-full">
          <label class="form-label">
            <span>{{ $t("isImperial.label") }}</span>
            <i class="fas fa-question-circle text-primary"
              v-tippy="{ arrow : true,  animation : 'shift-away'}"
              :content="$t('isImperial.helpText')">
            </i>
          </label>
          <!-- Unit cannot be changed in edit form -->
          <div v-if="isAdminMode">
            <div class="text-sm bg-primary rounded-full px-3 py-1 text-white inline-block mb-3">{{isImperial ? 'Yards' : 'Meters'}}</div>
            <div class="p-4 bg-gray-100 text-center border rounded text-sm text-danger mb-4 sm:mb-6">
              {{ $t('isImperial.adminModeWarning') }}
            </div>
          </div>
          <div v-else>
            <div class="flex flex-row flex-wrap justify-center mb-2 -mx-2">
              <div class="w-full lg:w-1/2 px-2">
                <label class="card transition mb-4 lg:mb-0 font-normal normal-case"
                  :class="{'is-selected': isImperial }">
                  <div class="p-4">
                    <div class="font-bold uppercase text-sm mb-1">
                      <span>{{$t('isImperial.imperial')}}</span>
                      <div class="absolute top-2 right-2" :class="{'hidden': !isImperial}">
                        <i class="fas fa-check-circle text-primary"></i>
                      </div>
                    </div>
                    <p class="text-xs sm:text-sm text-gray-700">{{$t('isImperial.imperialDescription')}}</p>
                    <input class="mr-1 hidden" type="radio" id="true" name="turret_customization[is_imperial]" :value="true" v-model="isImperial" />
                  </div>
                </label>
              </div>

              <div class="w-full lg:w-1/2 px-2">
                <label class="card transition mb-4 lg:mb-0 font-normal normal-case"
                  :class="{'is-selected': !isImperial }">
                  <div class="p-4">
                    <div class="font-bold uppercase text-sm mb-1">
                      <span>{{ $t('isImperial.metric') }}</span>
                      <div class="absolute top-2 right-2" :class="{'hidden': isImperial}">
                        <i class="fas fa-check-circle text-primary"></i>
                      </div>
                    </div>
                    <div class="text-xs sm:text-sm text-gray-700">{{$t('isImperial.metricDescription')}}</div>
                    <input class="mr-1 hidden" type="radio" id="false" name="turret_customization[is_imperial]" :value="false" v-model="isImperial" />
                  </div>
                </label>
              </div>
            </div>
            <div class="form-hint">{{ $t('isImperial.hintText') }}</div>
          </div>
        </div>

        <!-- Number of Revolutions -->
        <div class="form-group w-full lg:w-1/2 lg:pr-3">
          <label class="form-label">
            <span>{{ $t("numberOfRevolutions.label") }}</span>
            <i class="fas fa-question-circle text-primary"
              v-tippy="{ arrow : true,  animation : 'shift-away'}"
              :content="$t('numberOfRevolutions.helpText')">
            </i>
          </label>

          <select name="turret_customization[number_of_revolutions]" class="select with-caret" v-model="numberOfRevolutions">
            <option :value="revNumber" v-for="revNumber in maxNumberOfRevolutions">{{revNumber}}</option>
          </select>
        </div>

        <!-- Direction -->
        <div class="form-group w-full lg:w-1/2">
          <label class="form-label">
            <span>{{ $t("direction.label") }}</span>
            <i class="fas fa-question-circle text-primary"
              v-tippy="{ arrow : true,  animation : 'shift-away'}"
              :content="$t('direction.helpText')">
            </i>
          </label>

          <select
            name="turret_customization[direction]"
            class="form-control select with-caret"
            v-model="direction">
            <option value="clockwise">{{ $t("direction.clockwise") }}</option>
            <option value="counter_clockwise">{{ $t("direction.counterClockwise") }}</option>
          </select>
        </div>

        <div class="form-group w-full lg:w-1/3 lg:pr-3">
          <label class="form-label">
            <span>{{ $t("clickUnit.label") }}</span>
            <i class="fas fa-question-circle text-primary"
              v-tippy="{ arrow : true,  animation : 'shift-away'}"
              :content="$t('clickUnit.helpText')">
            </i>
          </label>

          <select
            name="turret_customization[click_unit]"
            class="form-control select with-caret"
            v-model="clickUnit">
            <option value="moa">MOA</option>
            <option value="mil">MIL</option>
          </select>
        </div>

        <div class="form-group w-full lg:w-1/3 lg:pr-3">
          <label class="form-label">
            <span>{{ $t('clickValue.label') }}</span>
            <i class="fas fa-question-circle text-primary"
              v-tippy="{ arrow : true,  animation : 'shift-away'}"
              :content="$t('clickValue.helpText')"
              data-tippy-animation="fade">
            </i>
          </label>

          <select
            name="turret_customization[click_value]"
            class="form-control select with-caret"
            v-model="clickValue">
            <option :value="option[0]" v-for="option in clickValueOptions">{{option[1]}}</option>
          </select>
        </div>

        <div class="form-group w-full lg:w-1/3">
          <label class="form-label">
            <span>{{ $t("clicksPerRotation.label") }}</span>
            <i class="fas fa-question-circle text-primary"
              v-tippy="{ arrow : true,  animation : 'shift-away'}"
              :content="$t('clicksPerRotation.helpText')">
            </i>
          </label>

          <input
            name="turret_customization[clicks_per_rotation]"
            type="number"
            step="any"
            class="form-control"
            :class="{'error': errors.has('turret_customization[clicks_per_rotation]')}"
            :placeholder="$t('clicksPerRotation.placeholder')"
            :data-vv-as="$t('clicksPerRotation.label')"
            v-validate="'required|min_value:0'"
            v-model="clicksPerRotation">
          </input>
          <span class="form-hint error">{{ errors.first("turret_customization[clicks_per_rotation]") }}</span>
        </div>

        <!-- Sight Height -->
        <div class="form-group w-full">
          <label class="form-label">
            <span>{{ $t("sightHeight.label") }}</span>
            <i class="fas fa-question-circle text-primary"
              v-tippy="{ arrow : true,  animation : 'shift-away'}"
              :content="$t('sightHeight.helpText')">
            </i>
          </label>

          <div class="input-group">
            <input
              name="turret_customization[sight_height]"
              type="number"
              step="any"
              class="form-control"
              :class="{'error': errors.has('turret_customization[sight_height]')}"
              :placeholder="$t('sightHeight.placeholder')"
              :data-vv-as="$t('sightHeight.label')"
              v-validate=sightHeightValidation
              v-model="sightHeight">
            </input>
            <div class="input-group-append">
              <div class="input-group-text text-xs uppercase">{{ isImperialTranslation }}</div>
            </div>
          </div>
          <span class="form-hint error">{{ errors.first("turret_customization[sight_height]") }}</span>
        </div>
      </div>

      <!-- Hidden Scope Slug Value -->
      <input name="turret_customization[scope_slug]" type="text" :value="permittedScopeSlug" hidden></input>

      <!-- Scope Note -->
      <div class="p-4 bg-gray-100 text-center border rounded text-sm text-gray-700 mb-4 sm:mb-6">
        {{ turretCustomizationEnabled ? $t("scope.customFormHelper") : $t("scope.formHelper") }}
      </div>
    </div>
    <!-- Turret Properties -->
    <div class="p-4 border-t" v-show="turretCustomizationEnabled">
      <!-- Lable Height & Width -->
      <div class="flex flex-col lg:flex-row lg-flex-wrap" >
        <div class="form-group w-full lg:w-1/2 lg:pr-3">
          <label class="form-label">
            <span>{{ $t("turretHeight.label") }}</span>
            <i class="fas fa-question-circle text-primary"
              v-tippy="{ arrow : true,  animation : 'shift-away'}"
              :content=turretHeightContent>
            </i>
          </label>
          <div class="input-group">
            <input
              name="turret_customization[turret_height]"
              type="number"
              step="any"
              class="form-control"
              :class="{'error': errors.has('turret_customization[turret_height]')}"
              :placeholder="$t('turretHeight.placeholder')"
              :data-vv-as="$t('turretHeight.label')"
              v-validate=turretHeightValidation
              v-model="turretHeight">
            </input>
            <div class="input-group-append">
              <div class="input-group-text text-xs uppercase">{{ isImperialTranslation }}</div>
            </div>
          </div>

          <span class="form-hint error">{{ errors.first("turret_customization[turret_height]") }}</span>
        </div>

        <div class="form-group w-full lg:w-1/2">
          <label class="form-label">
            <span>{{ $t("turretDiameter.label") }}</span>
            <i class="fas fa-question-circle text-primary"
              v-tippy="{ arrow : true,  animation : 'shift-away'}"
              :content=turretDiameterContent>
            </i>
          </label>

          <div class="input-group">
            <input
              name="turret_customization[turret_diameter]"
              type="number"
              step="any"
              class="form-control"
              :class="{'error': errors.has('turret_customization[turret_diameter]')}"
              :placeholder="$t('turretDiameter.placeholder')"
              :data-vv-as="$t('turretDiameter.label')"
              v-validate=turretDiameterValidation
              v-model="turretDiameter">
            </input>
            <div class="input-group-append">
              <div class="input-group-text text-xs uppercase">{{ isImperialTranslation }}</div>
            </div>
          </div>
          <span class="form-hint error">{{ errors.first("turret_customization[turret_diameter]") }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from "vuex"

const { mapGetters } = createNamespacedHelpers("turretCustomizer")

export default {
  props: ["isAdminMode", "turretCustomizationEnabled"],
  computed: {
    ...mapGetters([
      "currentBrand",
      "currentScope",
      "maxNumberOfRevolutions",
      "permittedCurrentScope"
    ]),
    isImperial: {
      get() { return this.$store.getters["turretCustomizer/isImperial"] },
      set(isImperialUpdate) { this.$store.dispatch("turretCustomizer/updateIsImperial", isImperialUpdate) }
    },
    turretHeight: {
      get() { return this.$store.getters["turretCustomizer/turretHeight"] },
      set(newTurretHeight) { this.$store.dispatch("turretCustomizer/updateTurretHeight", newTurretHeight) }
    },
    turretDiameter: {
      get() { return this.$store.getters["turretCustomizer/turretDiameter"] },
      set(newTurretDiameter) { this.$store.dispatch("turretCustomizer/updateTurretDiameter", newTurretDiameter) }
    },
    sightHeight: {
      get() { return this.$store.getters["turretCustomizer/sightHeight"] },
      set(newSightHeight) { this.$store.dispatch("turretCustomizer/updateSightHeight", newSightHeight) }
    },

    clickUnit: {
      get() { return this.$store.getters["turretCustomizer/clickUnit"] },
      set(newClickUnit) {
        this.$store.dispatch("turretCustomizer/updateClickUnit", newClickUnit)

        // Update value of click value select to the first option when user changes click unit.
        this.$store.dispatch("turretCustomizer/updateClickValue", this.clickValueOptions[0][0])
      }
    },
    clickValue: {
      get() { return this.$store.getters["turretCustomizer/clickValue"] },
      set(newClickValue) { this.$store.dispatch("turretCustomizer/updateClickValue", newClickValue) }
    },
    clicksPerRotation: {
      get() { return this.$store.getters["turretCustomizer/clicksPerRotation"] },
      set(newClicksPerRotation) { this.$store.dispatch("turretCustomizer/updateClicksPerRotation", newClicksPerRotation) }
    },
    direction: {
      get() { return this.$store.getters["turretCustomizer/direction"] },
      set(newDirection) { this.$store.dispatch("turretCustomizer/updateDirection", newDirection) }
    },
    numberOfRevolutions: {
      get() { return this.$store.getters["turretCustomizer/numberOfRevolutions"] },
      set(newNumberOfRevolutions) { this.$store.dispatch("turretCustomizer/updateNumberOfRevolutions", newNumberOfRevolutions) }
    },
    clickValueOptions() {
      if (this.clickUnit === "moa") {
        return [[0.5, '1/2'], [0.33, '1/3'], [0.25, '1/4'], [0.125, '1/8']]
      }

      return [0.05, 0.1, 0.20, 0.25, 0.33, 0.36, 0.75].map((item) => [item, item])
    },
    permittedScopeSlug() {
      return this.permittedCurrentScope.slug
    },
    sightHeightValidation() {
      return this.isImperial ? { required: true, between: [1, 4] } : { required: true, between: [2, 10] }
    },
    turretHeightContent() {
      const translation = this.isImperial ? this.$t('turretHeight.helpTextImperial') : this.$t('turretHeight.helpTextMetric')
      return `<img class=\"w-32 mx-auto\" src=\"https://i.imgur.com/yQK0WTa.png\" /><p>${translation}</p>`
    },
    turretHeightValidation() {
      return this.isImperial ? { required: true, between: [0.1, 2] } : { required: true, between: [0.3, 5] }
    },
    turretDiameterContent() {
      const translation = this.isImperial ? this.$t("turretDiameter.helpTextImperial") : this.$t("turretDiameter.helpTextMetric")
      return `<img class=\"w-32 mx-auto\" src=\"https://i.imgur.com/28hNopj.png\" /><p>${translation}</p>`
    },
    turretDiameterValidation() {
      return this.isImperial ? { required: true, between: [0.5, 3] } : { required: true, between: [1, 7] }
    },
    isImperialTranslation() {
      return this.isImperial ? this.$t('generic.inches') : this.$t('generic.centimeters')
    }
  }
}
</script>